import { collection, getDocs, query, where, orderBy, addDoc, doc, updateDoc, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentMillis } from '../utils/dateUtils';
import type { Role } from '../types/role';

export class RoleService {
  // Fetch all roles for a specific user
  static async getUserRoles(userId: string): Promise<Role[]> {
    try {
      console.log('🔍 Fetching roles for user:', userId);
      
      const rolesRef = collection(db, 'Users', userId, 'Roles');
      console.log('📁 Roles collection path:', `Users/${userId}/Roles`);
      
      // First, try to get all roles without any filters to see what's available
      const allRolesQuery = query(rolesRef);
      const allRolesSnapshot = await getDocs(allRolesQuery);
      console.log('📊 Total roles found (unfiltered):', allRolesSnapshot.size);
      
      // Log all documents to see their structure
      allRolesSnapshot.forEach((doc: QueryDocumentSnapshot) => {
        console.log('📄 Document ID:', doc.id, 'Data:', doc.data());
      });
      
      // Now apply the filter for non-deleted roles
      // Try multiple approaches to handle different data structures
      let rolesQuery;
      let querySnapshot;
      
      try {
        // First try: isDeleted == false
        rolesQuery = query(
          rolesRef,
          where('isDeleted', '==', false),
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(rolesQuery);
        console.log('✅ Approach 1 (isDeleted == false):', querySnapshot.size, 'roles found');
      } catch (error) {
        console.log('⚠️ Approach 1 failed, trying without isDeleted filter');
        
        try {
          // Second try: no isDeleted filter, just order by createdAt
          rolesQuery = query(
            rolesRef,
            orderBy('createdAt', 'desc')
          );
          querySnapshot = await getDocs(rolesQuery);
          console.log('✅ Approach 2 (no isDeleted filter):', querySnapshot.size, 'roles found');
        } catch (error2) {
          console.log('⚠️ Approach 2 failed, trying without orderBy');
          
          // Third try: no filters at all
          rolesQuery = query(rolesRef);
          querySnapshot = await getDocs(rolesQuery);
          console.log('✅ Approach 3 (no filters):', querySnapshot.size, 'roles found');
        }
      }
      
      const roles: Role[] = [];
      
      querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
        const data = doc.data();
        console.log('🔄 Processing role:', doc.id, data);
        
        // Skip roles that are marked as deleted
        if (data.isDeleted === true) {
          console.log('⏭️ Skipping deleted role:', doc.id);
          return;
        }
        
        const role: Role = {
          id: doc.id,
          officeID: data.officeID || '',
          officeName: data.officeName || '',
          officeLogoURL: data.officeLogoURL || '',
          officePhone: data.officePhone || '',
          lawyerID: data.lawyerID || '',
          lawyerName: data.lawyerName || '',
          lawyerPhotoURL: data.lawyerPhotoURL || '',
          isOfficeCreator: data.isOfficeCreator || false,
          isOfficeAdmin: data.isOfficeAdmin || false,
          subSpecialityID: data.subSpecialityID || '',
          lawyerTitleID: data.lawyerTitleID || '',
          salaryIndicator: data.salaryIndicator || 0,
          salaryValue: data.salaryValue || 0,
          profitPercentage: data.profitPercentage || 0,
          isArchived: data.isArchived || false,
          isDeleted: data.isDeleted || false,
          clientsReferences: data.clientsReferences || [],
          userPermission: data.userPermission || {
            canViewClients: false,
            canEditClients: false,
            canDeleteClients: false,
            canViewPayments: false,
            canEditPayments: false,
            canViewEvents: false,
            canEditEvents: false,
            canViewReports: false,
            canManageUsers: false,
            canManageRoles: false
          },
          createdAt: data.createdAt || Date.now().toString(),
          updatedAt: data.updatedAt || Date.now().toString(),
          createdBy: data.createdBy || '',
          updatedBy: data.updatedBy || '',
        };
        roles.push(role);
      });
      
      console.log('🎯 Final roles array:', roles);
      return roles;
    } catch (error) {
      console.error('❌ Error fetching user roles:', error);
      throw new Error('Failed to fetch user roles');
    }
  }

  // Get role by ID for a specific user
  static async getUserRole(userId: string, roleId: string): Promise<Role | null> {
    try {
      const roles = await this.getUserRoles(userId);
      return roles.find(role => role.id === roleId) || null;
    } catch (error) {
      console.error('Error fetching specific role:', error);
      throw new Error('Failed to fetch role details');
    }
  }

  // Get active roles (not archived or deleted)
  static async getActiveUserRoles(userId: string): Promise<Role[]> {
    try {
      const allRoles = await this.getUserRoles(userId);
      return allRoles.filter(role => !role.isArchived && !role.isDeleted);
    } catch (error) {
      console.error('Error fetching active roles:', error);
      throw new Error('Failed to fetch active roles');
    }
  }

  // Create a new role for a user
  static async createUserRole(userId: string, roleData: Omit<Role, 'id'>): Promise<string> {
    try {
      console.log('🔍 RoleService: Creating role for user:', userId, 'with data:', roleData);
      
      const rolesRef = collection(db, 'Users', userId, 'Roles');
      console.log('📝 RoleService: Collection path:', `Users/${userId}/Roles`);
      
      const roleDataWithTimestamps = {
        ...roleData,
        createdAt: getCurrentMillis().toString(),
        updatedAt: getCurrentMillis().toString(),
        createdBy: userId,
        updatedBy: userId
      };
      
      console.log('📝 RoleService: Role data with timestamps:', roleDataWithTimestamps);
      
      const docRef = await addDoc(rolesRef, roleDataWithTimestamps);
      
      console.log('✅ RoleService: Role created successfully with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ RoleService: Error creating role:', error);
      console.error('❌ RoleService: Error details:', {
        code: (error as any)?.code,
        message: (error as any)?.message,
        stack: (error as any)?.stack
      });
      throw new Error('Failed to create role');
    }
  }

  // Update an existing role
  static async updateRole(roleId: string, roleData: Partial<Role>, userId?: string): Promise<void> {
    try {
      console.log('🔍 RoleService: Updating role:', roleId, 'with data:', roleData);
      
      if (!userId) {
        throw new Error('User ID is required to update role');
      }
      
      const rolesRef = collection(db, 'Users', userId, 'Roles');
      const roleDocRef = doc(rolesRef, roleId);
      
      const updateData = {
        ...roleData,
        updatedAt: getCurrentMillis().toString(),
        updatedBy: userId
      };
      
      await updateDoc(roleDocRef, updateData);
      console.log('✅ RoleService: Role updated successfully');
    } catch (error) {
      console.error('❌ RoleService: Error updating role:', error);
      throw new Error('Failed to update role');
    }
  }
} 